import React, { Component } from 'react';
import styled from 'styled-components';
import { Observable } from 'rxjs';

import MessageContainer from './MessageContainer';
import BotAvatar from '../BotAvatar';
import renderAttachment from './renderAttachment';
import { updateScroll } from '../index';
import withIsMobile from '../../../context/withIsMobile';
import withConfig from '../../../context/withConfig';
import { checkIsInOperatorConversation } from './utils';
import PrevButtonSVG from '../../../components/icons/PrevButtonSVG';
import NextButtonSVG from '../../../components/icons/NextButtonSVG';

const CARD_MARGIN = 10;

const CarouselContainer = withConfig(styled(MessageContainer)`
  position: relative;
  overflow: hidden;
  margin-bottom: 0;
  max-width: ${props => props.config.carouselWidth || '100vw'};
`);

const Slider = styled.div`
  transform: translateX(-${props => props.xOffset}px);
  transition: 0.5s ease-in-out;

  display: flex;
  align-items: stretch;
`;

const ButtonContainer = styled.div`
  width: 38px;
  height: 73px;
  border-radius: 19px;

  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  background: white;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);

  display: flex;
  align-items: center;
  justify-content: center;

  ${props => props.right && 'right: 10px;'};
  ${props => props.left && 'left: 10px;'};
`;

const CardContainer = styled.div`
  width: ${props => props.width}px;
  margin: 0px 20px 10px 0px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  position: relative;
`;

class Carousel extends Component {
  constructor(props) {
    super(props);

    this.container = React.createRef();
    this.slider = React.createRef();
  }

  state = {
    activeCardIndex: 0,
    width: 0
  };

  componentDidMount() {
    const width = this.container.current.getBoundingClientRect().width;

    this.setState({ width });
  }

  componentDidUpdate() {
    updateScroll();
  }

  getDimensions = () => {
    const { width, activeCardIndex } = this.state;
    const { isMobile } = this.props;

    const avatarWidth = isMobile ? 44 : 64;
    const chatPanelRightMargin = isMobile ? 20 : 50;

    const cardWidth = width - avatarWidth - chatPanelRightMargin;
    const xOffset = activeCardIndex * (cardWidth + 2 * CARD_MARGIN);

    return { cardWidth, xOffset, width };
  };

  nextCard = () => {
    this.setState({
      activeCardIndex: this.state.activeCardIndex + 1
    });
  };

  handleSliderTouchStart = startEvent => {
    const { activity } = this.props;
    const { activeCardIndex } = this.state;
    const canGoNext = activeCardIndex !== activity.attachments.length - 1;

    const initialTouchX = startEvent.touches[0].pageX;
    const distanceThreshold = 30;

    const touchMoves$ = Observable.fromEvent(this.slider.current, 'touchmove')
      .takeUntil(Observable.fromEvent(this.slider.current, 'touchend'))
      .map(event => event.touches[0].pageX);

    if (canGoNext) {
      touchMoves$
        .filter(pageX => initialTouchX - pageX >= distanceThreshold)
        .take(1)
        .subscribe(this.nextCard);
    }

    const canGoBack = activeCardIndex !== 0;
    if (canGoBack) {
      touchMoves$
        .filter(pageX => pageX - initialTouchX >= distanceThreshold)
        .take(1)
        .subscribe(this.previousCard);
    }
  };

  previousCard = () => {
    this.setState({
      activeCardIndex: this.state.activeCardIndex - 1
    });
  };

  render() {
    const { activity, blockPosition } = this.props;
    const { activeCardIndex } = this.state;

    const dimensions = this.getDimensions();

    const showPrevious = activeCardIndex !== 0;
    const showNext = activeCardIndex !== activity.attachments.length - 1;

    return (
      <CarouselContainer ref={this.container}>
        <Slider
          xOffset={dimensions.xOffset}
          ref={this.slider}
          onTouchStart={this.handleSliderTouchStart}
        >
          <BotAvatar
            visible={blockPosition === 'end' || blockPosition === 'single'}
            inOperatorConversation={checkIsInOperatorConversation(activity)}
          />

          {activity.attachments.map((attachment, i) => {
            const sections = renderAttachment(activity, i).sections;

            return (
              <CardContainer key={i} width={dimensions.cardWidth}>
                {sections}
              </CardContainer>
            );
          })}
        </Slider>

        {showNext && (
          <ButtonContainer right data-test-type="carouselNextIcon" onClick={this.nextCard}>
            <NextButtonSVG />
          </ButtonContainer>
        )}

        {showPrevious && (
          <ButtonContainer left data-test-type="carouselPreviousIcon" onClick={this.previousCard}>
            <PrevButtonSVG />
          </ButtonContainer>
        )}
      </CarouselContainer>
    );
  }
}

export default withIsMobile(Carousel);



// WEBPACK FOOTER //
// ./src/components/MessageHistory/BotMessage/Carousel.js